import { getToken } from "../storage/authStorage";
import { BillsResponseDTO, ZohoBillDetailsResponseDTO } from "../models/BillDTOs";
import { API_BASE_URL } from "../../../config/api";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

export const getBillsForVendor = async (
  vendorZohoId: string,
  limit?: number,
  offset?: number,
  search?: string
): Promise<BillsResponseDTO> => {
  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append("vendor_id", vendorZohoId);
    if (limit !== undefined) {
      params.append("limit", limit.toString());
    }
    if (offset !== undefined) {
      params.append("offset", offset.toString());
    }
    if (search !== undefined && search.trim() !== "") {
      params.append("search", search.trim());
    }

    const response = await fetch(
      `${API_BASE_URL}/api/bills/getBillsForVendor?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: "Failed to parse error response" };
      }

      throw new APIError(
        errorData.message || "Failed to fetch vendor bills",
        response.status,
        errorData
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching vendor bills:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

export const getZohoBillDetails = async (
  zohoBillId: string
): Promise<ZohoBillDetailsResponseDTO> => {
  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append("zoho_billid", zohoBillId);

    const response = await fetch(
      `${API_BASE_URL}/api/bills/accounting-bill-details?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: "Failed to parse error response" };
      }

      throw new APIError(
        errorData.message || "Failed to fetch Zoho bill details",
        response.status,
        errorData
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching Zoho bill details:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

